import { Button, Paper, Popover, Stack, Typography } from '@mui/material';
import { CustomCellEditorProps } from 'ag-grid-react';
import { cloneDeep } from 'lodash-es';
import { KeyboardEvent, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormFieldWithLabelFactory } from '../../../../../components/Form/FormFieldAndLabelFactory';
import { KpiSection } from '../../../../../data-models/company-financials.data-model';
import { field2ToFormField } from '../../../../../data-models/field2.data-model';
import { useFocusElement } from '../../../../../hooks/useFocusElement';
import { isInvalidDate } from '../../../../../util/date-utilts';
import { formatISODateOnly } from '../../../../../util/formatters/DateFormatters';
import { getFormattedFiscalDate } from '../../../utils/financialUtils';
import {
  getFinancialsFromRowData,
  HeaderCompParamsType,
  IValuesBySection,
  TKpiValueSetterParams,
  useKpiValueSetter,
} from '../hooks/useFinancialsColumnDefs';
import { IFinancialsRowData2 } from '../hooks/useFinancialsGridData';

export const calculatedDescription =
  'This field is calculated from monthly or quarterly values. Please update the data in those periods for consistency.';
export interface IKpiEditorParams extends CustomCellEditorProps<
  IFinancialsRowData2,
  IValuesBySection<unknown>
> {
  fye: number;
}

export function KpiEditor(params: IKpiEditorParams) {
  const { data, value, onValueChange, colDef, stopEditing, fye } = params;
  const { colDate, kpiPeriod } = colDef.headerComponentParams as HeaderCompParamsType;
  const [today] = useState(() => Date.now());
  const { valuesByDate2, ...kpi } = data ?? {};
  const financials = getFinancialsFromRowData(data, formatISODateOnly(colDate), kpiPeriod);
  const isActualCalculated = !!financials?.actual?.calculated;
  const isBudgetCalculated = !!financials?.budget?.calculated;

  const methods = useForm<IValuesBySection<unknown>>({
    values: cloneDeep(value) as IValuesBySection<unknown>,
  });

  const field = field2ToFormField(kpi);
  const actualField = cloneDeep({
    ...field,
    name: KpiSection.actual,
    label: 'Actual',
    key: KpiSection.actual,
    variant: 'form' as const,
    description: isActualCalculated ? calculatedDescription : undefined,
    disabled: colDate.getTime() > today || isActualCalculated,
  });
  const budgetField = cloneDeep({
    ...field,
    name: KpiSection.budget,
    label: 'Budget',
    key: KpiSection.budget,
    variant: 'form' as const,
    description: isBudgetCalculated ? calculatedDescription : undefined,
    disabled: isBudgetCalculated,
  });

  const [open, setOpen] = useState(true);
  const valueSetter = useKpiValueSetter();

  const onClose = useCallback(() => {
    setOpen(false);
    stopEditing();
  }, [stopEditing]);

  const onSave = useCallback(() => {
    const newValue = methods.getValues();
    if (!validateValue(newValue.actual) || !validateValue(newValue.budget)) {
      return;
    }
    valueSetter({ data, colDef, newValue } as TKpiValueSetterParams);
    onValueChange({
      ...value,
      actual: newValue.actual ?? null,
      budget: newValue.budget ?? null,
    } as IValuesBySection<unknown>);
    setOpen(false);
    stopEditing();
  }, [colDef, data, methods, onValueChange, stopEditing, value, valueSetter]);

  useFocusElement('kpi-editor', 'input');

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key == 'Escape') {
        onClose();
      } else if (e.key === 'Enter') {
        onSave();
      }
    },
    [onClose, onSave]
  );

  const formattedDate = `${data.displayName} (${getFormattedFiscalDate(
    formatISODateOnly(colDate),
    kpiPeriod,
    fye ?? 12
  )})`;

  if (!data || !value || !open) {
    return <></>;
  }

  // No `disablePortal` on the Popover below: ag-grid v35 makes
  // `.ag-popup-editor` (the cellEditorPopup wrapper that hosts this component)
  // `position: absolute`, which turns it into the offset parent for any
  // in-tree positioned descendants. MUI's Popover Paper uses `position: absolute`
  // with top/left computed from `anchorEl.getBoundingClientRect()` (viewport
  // coords) — with disablePortal those viewport coords get re-anchored to the
  // wrapper and the popup lands ~`cellTop + cellTop` down / `cellLeft + cellLeft`
  // right. Portaling to `document.body` keeps absolute positioning viewport-
  // relative, which is what we want.
  return (
    <Popover open={true} anchorEl={params.eGridCell} onKeyDown={onKeyDown}>
      <Paper style={{ minWidth: '20rem', padding: '1rem' }}>
        <Typography variant='body2' mb='0.5rem'>
          {formattedDate}
        </Typography>
        <FormProvider {...methods}>
          <Stack id='kpi-editor'>
            <FormFieldWithLabelFactory formField={actualField} />
            <FormFieldWithLabelFactory formField={budgetField} />
          </Stack>
          <Stack direction='row' spacing={1} justifyContent='start' mt='0.5rem'>
            <Button variant='contained' color='secondary' onClick={onSave}>
              Save
            </Button>
            <Button variant='outlined' color='secondary' onClick={onClose}>
              Cancel
            </Button>
          </Stack>
        </FormProvider>
      </Paper>
    </Popover>
  );
}

function validateValue(value: unknown) {
  if (isInvalidDate(value)) {
    return false;
  }
  return true;
}
